import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  BookOpen, 
  ArrowLeft, 
  Users, 
  CheckCircle2, 
  XCircle, 
  Shield,
  Loader2,
  Mail,
  Phone,
  Calendar,
  Key,
  Copy
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface UserData {
  id: string;
  full_name: string;
  email: string;
  mobile: string | null;
  created_at: string;
  payment_status: 'success' | 'failed' | 'pending' | 'none';
  auto_password: string | null;
}

const AdminPanel: React.FC = () => {
  const { user, isAdmin, isLoading, signOut } = useSupabaseAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    paidUsers: 0,
    failedPayments: 0,
  });
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/admin/login');
      return;
    }

    if (!isLoading && user && !isAdmin) {
      navigate('/');
      return;
    }

    if (user && isAdmin) {
      fetchAdminData();
    }
  }, [user, isAdmin, isLoading, navigate]);

  const fetchAdminData = async () => {
    setLoadingData(true);
    try {
      // Fetch all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
        return;
      }

      // Fetch all payments
      const { data: payments, error: paymentsError } = await supabase
        .from('payments')
        .select('*');

      if (paymentsError) {
        console.error('Error fetching payments:', paymentsError);
        return;
      }

      // Map profiles with payment status
      const usersWithPayments: UserData[] = (profiles || []).map(profile => {
        const userPayments = payments?.filter(p => p.user_id === profile.id) || [];
        let paymentStatus: 'success' | 'failed' | 'pending' | 'none' = 'none';
        
        if (userPayments.some(p => p.status === 'success')) {
          paymentStatus = 'success';
        } else if (userPayments.some(p => p.status === 'failed')) {
          paymentStatus = 'failed';
        } else if (userPayments.some(p => p.status === 'pending')) {
          paymentStatus = 'pending';
        }

        return {
          id: profile.id,
          full_name: profile.full_name,
          email: profile.email,
          mobile: profile.mobile,
          created_at: profile.created_at,
          payment_status: paymentStatus,
          auto_password: (profile as any).auto_password || null,
        };
      });

      setUsers(usersWithPayments);

      // Calculate stats
      const paidCount = usersWithPayments.filter(u => u.payment_status === 'success').length;
      const failedCount = usersWithPayments.filter(u => u.payment_status === 'failed').length;

      setStats({
        totalUsers: usersWithPayments.length,
        paidUsers: paidCount,
        failedPayments: failedCount,
      });

    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-600">
            <CheckCircle2 className="h-3 w-3" />
            Paid
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-600">
            <XCircle className="h-3 w-3" />
            Failed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/10 px-2.5 py-1 text-xs font-medium text-yellow-600">
            <Loader2 className="h-3 w-3" />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
            No Payment
          </span>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Panel - TOPPERS BUDDY</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-subtle">
        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur">
          <div className="container flex h-16 items-center justify-between px-4 md:px-6">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                className="gap-2"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-600">
                <Shield className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">
                <span className="text-gradient">ADMIN</span>
                <span className="text-foreground"> PANEL</span>
              </span>
            </div>

            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground"
            >
              Logout
            </Button>
          </div>
        </header>

        {/* Main Content */}
        <main className="container px-4 py-8 md:px-6 md:py-12">
          <div className="mx-auto max-w-6xl">
            {/* Stats Cards */}
            <div className="mb-8 grid gap-4 md:grid-cols-3">
              {/* Total Users */}
              <div className="rounded-2xl border border-border/50 bg-card p-6 shadow-card">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                    <Users className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Registered Users</p>
                    <p className="text-3xl font-bold text-foreground">{stats.totalUsers}</p>
                  </div>
                </div>
              </div>

              {/* Paid Users */}
              <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-6 shadow-card">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-500/20">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Paid Users</p>
                    <p className="text-3xl font-bold text-green-600">{stats.paidUsers}</p>
                  </div>
                </div>
              </div>

              {/* Failed Payments */}
              <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-6 shadow-card">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-red-500/20">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Failed Payments</p>
                    <p className="text-3xl font-bold text-red-600">{stats.failedPayments}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="rounded-2xl border border-border/50 bg-card shadow-card overflow-hidden">
              <div className="border-b border-border/50 p-6">
                <h2 className="text-xl font-bold text-foreground">User Data</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  All registered users and their payment status
                </p>
              </div>

              {loadingData ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No users registered yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Password</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Payment Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.full_name || 'N/A'}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              {user.email}
                            </span>
                          </TableCell>
                          <TableCell>
                            {user.auto_password ? (
                              <span className="flex items-center gap-2">
                                <Key className="h-4 w-4 text-muted-foreground" />
                                <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">
                                  {user.auto_password}
                                </code>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(user.auto_password!);
                                  }}
                                  className="p-1 hover:bg-muted rounded"
                                  title="Copy password"
                                >
                                  <Copy className="h-3 w-3 text-muted-foreground" />
                                </button>
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">Manual signup</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-muted-foreground" />
                              {user.mobile || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              {new Date(user.created_at).toLocaleDateString()}
                            </span>
                          </TableCell>
                          <TableCell>{getStatusBadge(user.payment_status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminPanel;
